import type {
  BrowserPatterns,
  VersionPatterns,
  OSPatterns,
  PlatformPatterns,
  DeviceUUIDOptions,
  AgentInfo,
} from '../types';

/**
 * List of known bot user agents
 */
export const BOTS: readonly string[] = [
  '\\+https:\\/\\/developers.google.com\\/\\+\\/web\\/snippet\\/',
  'googlebot',
  'baiduspider',
  'gurujibot',
  'yandexbot',
  'slurp',
  'msnbot',
  'bingbot',
  'facebookexternalhit',
  'linkedinbot',
  'twitterbot',
  'slackbot',
  'telegrambot',
  'applebot',
  'pingdom',
  'tumblr ',
  'Embedly',
  'spbot',
] as const;

/**
 * Compiled regex for bot detection
 */
export const IS_BOT_REGEXP = new RegExp(`^.*(${BOTS.join('|')}).*$`);

/**
 * Browser version detection patterns
 */
export const VERSION_PATTERNS: Readonly<VersionPatterns> = {
  Edge: /(?:Edge|Edg)\/([\d\w.-]+)/i,
  Firefox: /firefox\/([\d\w.-]+)/i,
  IE: /msie\s([\d.]+[\d])|trident\/\d+\.\d+;.*[rv:]+(\d+\.\d)/i,
  Chrome: /chrome\/([\d\w.-]+)/i,
  Chromium: /(?:chromium|crios)\/([\d\w.-]+)/i,
  Safari: /version\/([\d\w.-]+)/i,
  Opera: /version\/([\d\w.-]+)|OPR\/([\d\w.-]+)/i,
  Ps3: /([\d\w.-]+)\)\s*$/i,
  Psp: /([\d\w.-]+)\)?\s*$/i,
  Amaya: /amaya\/([\d\w.-]+)/i,
  SeaMonkey: /seamonkey\/([\d\w.-]+)/i,
  OmniWeb: /omniweb\/v([\d\w.-]+)/i,
  Flock: /flock\/([\d\w.-]+)/i,
  Epiphany: /epiphany\/([\d\w.-]+)/i,
  WinJs: /msapphost\/([\d\w.-]+)/i,
  PhantomJS: /phantomjs\/([\d\w.-]+)/i,
  UC: /UCBrowser\/([\d\w.]+)/i,
} as const;

/**
 * Browser detection patterns
 */
export const BROWSER_PATTERNS: Readonly<BrowserPatterns> = {
  Edge: /edge|edg\//i,
  Amaya: /amaya/i,
  Konqueror: /konqueror/i,
  Epiphany: /epiphany/i,
  SeaMonkey: /seamonkey/i,
  Flock: /flock/i,
  OmniWeb: /omniweb/i,
  Chromium: /chromium|crios/i,
  Chrome: /chrome/i,
  Safari: /safari/i,
  IE: /msie|trident/i,
  Opera: /opera|OPR/i,
  PS3: /playstation 3/i,
  PSP: /playstation portable/i,
  Firefox: /firefox/i,
  WinJs: /msapphost/i,
  PhantomJS: /phantomjs/i,
  UC: /UCBrowser/i,
} as const;

/**
 * Operating system detection patterns
 */
export const OS_PATTERNS: Readonly<OSPatterns> = {
  Windows11: /windows nt 10\.0/i,
  Windows10: /windows nt 10\.0/i,
  Windows81: /windows nt 6\.3/i,
  Windows8: /windows nt 6\.2/i,
  Windows7: /windows nt 6\.1/i,
  UnknownWindows: /windows nt 6\.\d+/i,
  WindowsVista: /windows nt 6\.0/i,
  Windows2003: /windows nt 5\.2/i,
  WindowsXP: /windows nt 5\.1/i,
  Windows2000: /windows nt 5\.0/i,
  WindowsPhone8: /windows phone 8\./,
  OSXCheetah: /os x 10[._]0/i,
  OSXPuma: /os x 10[._]1(\D|$)/i,
  OSXJaguar: /os x 10[._]2/i,
  OSXPanther: /os x 10[._]3/i,
  OSXTiger: /os x 10[._]4/i,
  OSXLeopard: /os x 10[._]5/i,
  OSXSnowLeopard: /os x 10[._]6/i,
  OSXLion: /os x 10[._]7/i,
  OSXMountainLion: /os x 10[._]8/i,
  OSXMavericks: /os x 10[._]9/i,
  OSXYosemite: /os x 10[._]10/i,
  OSXElCapitan: /os x 10[._]11/i,
  OSXSierra: /os x 10[._]12/i,
  OSXHighSierra: /os x 10[._]13/i,
  OSXMojave: /os x 10[._]14/i,
  OSXCatalina: /os x 10[._]15/i,
  MacOSBigSur: /mac os x 1[01][._](?:16|[6-9]|\d{2})|os x 10[._]16/i,
  MacOSMonterey: /mac os x 12[._]/i,
  MacOSVentura: /mac os x 13[._]/i,
  MacOSSonoma: /mac os x 14[._]/i,
  MacOSSequoia: /mac os x 15[._]/i,
  Mac: /os x|mac os/i,
  Linux: /linux/i,
  Linux64: /linux x86_64/i,
  ChromeOS: /cros/i,
  Wii: /wii/i,
  PS3: /playstation 3/i,
  PSP: /playstation portable/i,
  iPad: /\(ipad\b/i,
  iPhone: /\(iphone\b/i,
  Bada: /Bada\/(\d+)\.(\d+)/i,
  Curl: /curl\/(\d+)\.(\d+)\.(\d+)/i,
} as const;

/**
 * Platform detection patterns
 */
export const PLATFORM_PATTERNS: Readonly<PlatformPatterns> = {
  Windows: /windows nt/i,
  WindowsPhone: /windows phone/i,
  Mac: /macintosh/i,
  Linux: /linux/i,
  Wii: /wii/i,
  Playstation: /playstation/i,
  iPad: /ipad/i,
  iPod: /ipod/i,
  iPhone: /iphone/i,
  Android: /android/i,
  Blackberry: /blackberry/i,
  Samsung: /samsung/i,
  Curl: /curl/i,
} as const;

/**
 * Default configuration options
 */
export const DEFAULT_OPTIONS: Readonly<DeviceUUIDOptions> = {
  version: false,
  language: false,
  platform: true,
  os: true,
  pixelDepth: true,
  colorDepth: true,
  resolution: false,
  isAuthoritative: true,
  silkAccelerated: true,
  isKindleFire: true,
  isDesktop: true,
  isMobile: true,
  isTablet: true,
  isWindows: true,
  isLinux: true,
  isLinux64: true,
  isChromeOS: true,
  isMac: true,
  isiPad: true,
  isiPhone: true,
  isiPod: true,
  isAndroid: true,
  isSamsung: true,
  isSmartTV: true,
  isRaspberry: true,
  isBlackberry: true,
  isTouchScreen: true,
  isOpera: false,
  isIE: false,
  isEdge: false,
  isIECompatibilityMode: false,
  isSafari: false,
  isFirefox: false,
  isWebkit: false,
  isChrome: false,
  isKonqueror: false,
  isOmniWeb: false,
  isSeaMonkey: false,
  isFlock: false,
  isAmaya: false,
  isPhantomJS: false,
  isEpiphany: false,
  source: false,
  cpuCores: false,
} as const;

/**
 * Default agent information
 */
export const DEFAULT_AGENT: Readonly<Omit<AgentInfo, 'hashInt' | 'hashMD5'>> = {
  isAuthoritative: true,
  isMobile: false,
  isTablet: false,
  isiPad: false,
  isiPod: false,
  isiPhone: false,
  isAndroid: false,
  isBlackberry: false,
  isOpera: false,
  isIE: false,
  isEdge: false,
  isIECompatibilityMode: false,
  isSafari: false,
  isFirefox: false,
  isWebkit: false,
  isChrome: false,
  isKonqueror: false,
  isOmniWeb: false,
  isSeaMonkey: false,
  isFlock: false,
  isAmaya: false,
  isPhantomJS: false,
  isEpiphany: false,
  isDesktop: false,
  isWindows: false,
  isLinux: false,
  isLinux64: false,
  isMac: false,
  isChromeOS: false,
  isBada: false,
  isSamsung: false,
  isRaspberry: false,
  isBot: false,
  isCurl: false,
  isAndroidTablet: false,
  isWinJs: false,
  isKindleFire: false,
  isSilk: false,
  isCaptive: false,
  isSmartTV: false,
  isUC: false,
  isTouchScreen: false,
  silkAccelerated: false,
  colorDepth: -1,
  pixelDepth: -1,
  resolution: [0, 0],
  cpuCores: -1,
  language: 'unknown',
  browser: 'unknown',
  version: 'unknown',
  os: 'unknown',
  platform: 'unknown',
  geoIp: {},
  source: '',
} as const;
